import { useEffect, useState } from "react";

import Table from "@mui/material/Table";
import LinkIcon from "@mui/icons-material/Link";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import DeleteIcon from "@mui/icons-material/Delete";
import { Folder } from "@mui/icons-material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { visuallyHidden } from "@mui/utils";
import {
  Box,
  Collapse,
  Container,
  IconButton,
  Paper,
  Switch,
  TableContainer,
  TablePagination,
  TableSortLabel,
  TextField
} from "@mui/material";

import conf from "../../conf";
import Create from "./Create";
import Manage from "./Manage";
import Refresh from "../utils/Refresh";
import useOpen from "../../hooks/useOpen";
import InfoChip from "../utils/InfoChip";
import { useAuth } from "../auth/AuthProvider";
import TagsWithEdit from "../utils/Tags";
import LinearLoader from "../utils/LinearLoader";
import SectionHeader from "../utils/SectionHeader";
import usePagination from "../../hooks/pagination";
import EmptyTableRow from "../utils/Empty";
import ProtectedRoute from "../auth/ProtectedRoute";
import ButtonWithConfirm from "../utils/ButtonWithConfirm";
import DescriptionWithEdit from "../utils/Description";
import {
  useProxyProjects,
  Project,
  useProxyProjectReset,
  useProxyProjectRemove,
  useProxyProjectsActivate,
  useProxyProjectsDeactivate
} from "../../hooks/proxy/projects";

const COLUMNS = conf.COLUMNS.PROJECTS;

const ProjectRow = ({ project }: { project: Project }) => {
  const { id, tags, name, online, description, activeUsers, latestUseId } = project;

  const reset = useProxyProjectReset();
  const remove = useProxyProjectRemove();
  const activate = useProxyProjectsActivate();
  const deactivate = useProxyProjectsDeactivate();

  const handleReset = () => reset.mutate({ id });
  const handleRemove = () => remove.mutate({ id });
  const handleAccessChange = (checked: boolean) => {
    // if checked (true), activate project, else deactivate
    checked ? activate.mutate({ id }) : deactivate.mutate({ id });
  };

  const { user: self } = useAuth();
  const readOnly = self.activeOrganization.role === "org.auditor";

  const messages = {
    reset: (
      <>
        This will reset all key artifacts for project: <b>{project.name}</b>. This action cannot be undone.
      </>
    ),
    remove: (
      <>
        This will remove all artifacts for project: <b>{project.name}</b>. This action cannot be undone.
      </>
    )
  };

  return (
    <TableRow sx={conf.STYLE.LAST_CHILD_NO_BORDER}>
      <TableCell scope="row">
        <InfoChip placement="left" label={name} sx={{ mb: 1 }} />

        <DescriptionWithEdit id={id} type="project" readOnly={readOnly} description={description} />
      </TableCell>

      <TableCell align="right">{project.acronym ?? "--"}</TableCell>

      <TableCell align="right">
        <TagsWithEdit id={id} type="project" tags={tags} readOnly={readOnly} />
      </TableCell>

      <TableCell align="right">{activeUsers ?? "--"}</TableCell>

      <TableCell align="right">{latestUseId ?? "--"}</TableCell>

      <TableCell align="right">
        <Switch
          color={online ? "success" : "error"}
          checked={online}
          disabled={readOnly}
          onChange={(e, checked) => handleAccessChange(checked)}
        />
      </TableCell>

      <TableCell align="right">
        <Manage fullWidth project={project} disabled={readOnly} />
      </TableCell>

      <TableCell align="right">
        <ButtonWithConfirm
          type="reset"
          label="Reset"
          variant="contained"
          status={reset.status}
          message={messages.reset}
          isLoading={reset.isPending}
          onConfirm={handleReset}
        />
      </TableCell>

      <TableCell align="right">
        <ButtonWithConfirm
          type="delete"
          label="Delete"
          variant="contained"
          status={remove.status}
          message={messages.remove}
          isLoading={remove.isPending}
          onConfirm={handleRemove}
        />
      </TableCell>
    </TableRow>
  );
};

type ProjectsProps = { collapsible?: boolean };

const Projects = ({ collapsible = false }: ProjectsProps) => {
  const { user } = useAuth();
  const isManager = user.activeOrganization.role === "org.project.manager";

  const { open, toggle } = useOpen(true);
  const [name, setName] = useState<string>("");

  const paginate = usePagination({ defaultSort: "acronym" });
  const { order, sort, page, limit, body } = paginate;
  const { handleChangeLimit, handleChangePage, handleSort } = paginate;

  const projects = useProxyProjects({ ...body, name });

  useEffect(() => {
    document.title = "[semiauth] | projects";
  }, []); // run once on mount

  return (
    <ProtectedRoute>
      <Container maxWidth="xl" sx={{ marginBottom: 4 }}>
        <SectionHeader
          title="Projects"
          icon={<Folder />}
          buttons={[
            ...(isManager ? [<Create key={"project-create"} />] : []),
            <TextField
              key={"projects-name-search"}
              type="text"
              size="small"
              placeholder="Search ..."
              value={name}
              onChange={(e) => setName(e.target.value)}
            />,
            <TablePagination
              key={"projects-pagination"}
              component="div"
              page={page}
              count={projects?.data?.meta.total || 0}
              rowsPerPage={limit}
              rowsPerPageOptions={[10, 25, 50, 100]}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeLimit}
            />,
            <Refresh key={"projects-refresh"} onClick={projects.refetch} isFetching={projects.isFetching} />,
            ...(collapsible
              ? [
                  <IconButton
                    key={"projects-collapse"}
                    size="small"
                    onClick={toggle}
                    aria-label="expand"
                    sx={{
                      transform: open ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.3s"
                    }}
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                ]
              : [])
          ]}
        />

        <Collapse in={open}>
          <Paper elevation={3}>
            <TableContainer sx={{ maxHeight: 630 }}>
              <Table aria-label="collapsible table" stickyHeader>
                <TableHead>
                  <TableRow>
                    {COLUMNS.BASE.map((column) => (
                      <TableCell
                        key={column.label}
                        sortDirection={order}
                        style={{ whiteSpace: "nowrap" }}
                        align={column.isHeader ? "left" : "right"}
                      >
                        <TableSortLabel
                          active={sort === column.key}
                          direction={sort === column.key ? order : "asc"}
                          onClick={(event: React.MouseEvent<unknown>) => handleSort(event, column.key)}
                        >
                          <b>{column.label}</b>
                          {sort === column.key ? (
                            <Box component="span" sx={visuallyHidden}>
                              {order === "desc" ? "sorted descending" : "sorted ascending"}
                            </Box>
                          ) : null}
                        </TableSortLabel>
                      </TableCell>
                    ))}

                    <TableCell align="right">
                      <LinkIcon fontSize="inherit" />
                    </TableCell>

                    <TableCell align="right">
                      <RestartAltIcon fontSize="inherit" />
                    </TableCell>

                    <TableCell align="right">
                      <DeleteIcon fontSize="inherit" />
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {projects.isLoading ? (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <LinearLoader />
                      </TableCell>
                    </TableRow>
                  ) : projects?.data?.data.length === 0 ? (
                    <EmptyTableRow type="projects" colSpan={8} />
                  ) : (
                    projects?.data?.data.map((project) => <ProjectRow key={project.id} project={project} />)
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Collapse>
      </Container>
    </ProtectedRoute>
  );
};

export default Projects;
